import React, {useState} from 'react';
import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {DropdownQuestion, Option} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import {useTranslation} from 'react-i18next';

interface DropdownEditorProps {
    question: DropdownQuestion;
    onQuestionUpdate: (question: DropdownQuestion) => void;
}

const DropdownEditor: React.FC<DropdownEditorProps> = ({
                                                       question,
                                                       onQuestionUpdate
                                                   }) => {
    const {t} = useTranslation();
    const [optionMenuAnchorEl, setOptionMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Generate a unique ID for new options
    const generateUniqueId = () => Math.random().toString(36).substring(2, 15);

    // Handle title update
    const handleTitleUpdate = (title: string) => {
        onQuestionUpdate({
            ...question,
            title
        });
    };

    // Handle description update
    const handleDescriptionUpdate = (description?: string) => {
        onQuestionUpdate({
            ...question,
            description: description || ''
        });
    };

    // Handle option text update
    const handleOptionUpdate = (optionId: string, text: string) => {
        const updatedOptions = question.options?.map(opt =>
            opt.id === optionId ? {...opt, label: text} : opt
        );

        onQuestionUpdate({
            ...question,
            options: updatedOptions,
        });
    };

    // Handle add new option
    const handleAddOption = () => {
        const newOption: Option = {
            id: generateUniqueId(),
            label: t('Option {{number}}', {number: (question.options?.length || 0) + 1}),
        };

        onQuestionUpdate({
            ...question,
            options: [...(question.options || []), newOption],
        });
    };

    // Handle delete option
    const handleDeleteOption = (optionId: string) => {
        onQuestionUpdate({
            ...question,
            options: question.options?.filter(opt => opt.id !== optionId),
        });
        handleOptionMenuClose();
    };

    // Handle opening option menu
    const handleOptionMenuOpen = (event: React.MouseEvent<HTMLElement>, optionId: string) => {
        event.stopPropagation();
        setOptionMenuAnchorEl(event.currentTarget);
        setSelectedOptionId(optionId);
    };

    // Handle closing option menu
    const handleOptionMenuClose = () => {
        setOptionMenuAnchorEl(null);
        setSelectedOptionId(null);
    };

    // Toggle dropdown open/close
    const handleToggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                p: 2,
            }}
        >
            {/* Title */}
            <EditableTypography
                value={question.title}
                onChange={handleTitleUpdate}
                multiline
                variant="h6"
                sx={{
                    mb: 2,
                    width: '100%',
                    fontWeight: 'medium',
                    textAlign: 'left',
                    padding: '8px',
                    borderRadius: 1,
                }}
                placeholder={t('Dropdown Question')}
            />

            {/* Description */}
            <Box sx={{mb: 3, width: '100%'}}>
                <MdEditor
                    value={question.description || ''}
                    onChange={handleDescriptionUpdate}
                    placeholder={t('Question description (optional)')}
                    className={'mini-mdx'}
                />
            </Box>

            {/* Dropdown Selector */}
            <FormControl fullWidth sx={{mb: 2}}>
                <Select
                    displayEmpty
                    value=""
                    disabled
                    sx={{borderRadius: 1}}
                >
                    <MenuItem value="" disabled>
                        <em>{question.options?.length ? t('Select an option') : t('No options available')}</em>
                    </MenuItem>
                </Select>
            </FormControl>

            {/* Options List */}
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <List sx={{p: 0}}>
                    {question.options?.map((option, index) => (
                        <ListItem
                            key={option.id}
                            divider={index < question.options!.length - 1}
                            secondaryAction={
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={(e) => handleOptionMenuOpen(e, option.id)}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={
                                    <EditableTypography
                                        value={option.label}
                                        onChange={value => handleOptionUpdate(option.id, value)}
                                        placeholder={t('Option {{number}}', {number: index + 1})}
                                        sx={{
                                            p: 1,
                                            borderRadius: 1,
                                            color: 'text.secondary',
                                        }}
                                    />
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {/* Add Option Button */}
            <Button
                startIcon={<AddIcon/>}
                onClick={handleAddOption}
                variant="outlined"
                sx={{
                    borderStyle: 'dashed',
                    textTransform: 'none',
                    borderRadius: 1,
                    py: 1,
                    width: '100%',
                    justifyContent: 'flex-start',
                }}
            >
                {t('Add option')}
            </Button>

            {/* Option Context Menu */}
            <Menu
                anchorEl={optionMenuAnchorEl}
                open={Boolean(optionMenuAnchorEl)}
                onClose={handleOptionMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (selectedOptionId) handleDeleteOption(selectedOptionId);
                    }}
                    dense
                >
                    <DeleteIcon fontSize="small" sx={{mr: 1}}/>
                    {t('Delete')}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default DropdownEditor;
